import React from "react";
//#region redux
import { useDispatch } from "react-redux";
import { setTodoListNavigation } from "store/actions/todoNavigationActions";
//#endregion
//#region  types
import { IconDefinitionConstants, RouteConstants } from "utils/constants";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
import { DeadlineWidgetProps } from "models/propTypes/todoPropTypes";
import { TranslationKeys } from "translations";
//#endregion
//#region  components
import { Grid, Hidden, Skeleton } from "@mui/material";
import { useTodosFromState } from "components/todos";
import { TodoChipContainer, useTodoGroupingHelpers } from "components/todos/_partials";
import lazyWithReload from "utils/lazyWithReload";
import { Icon } from "components/common";
import { useLocation, useNavigate } from "react-router-dom";
//#endregion
import { LocalizationHelperInstance, UrlHelper } from "utils/helpers";
import clsx from "clsx";
import cssExports from "theme/styles/main.module.scss";

const BannerImageLazy = lazyWithReload(() => import("./deadlineBannerImage"));

const DeadlineReportWidget: React.FC<DeadlineWidgetProps> = (props) => {
  const { deadline } = props;

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { todoCount, items } = useTodosFromState();
  const { getMockApiResponse } = useTodoGroupingHelpers();
  const [messages, setMessages] = React.useState<string[]>();

  React.useEffect(() => {
    if (deadline.consequences?.length) {
      const actions = deadline.consequences
        .slice(0, 2)
        .map((c) => LocalizationHelperInstance.getTranslatedEnumValue("Todos.ConsequenceAction", c.consequenceType));
      if (actions?.length) setMessages(actions);
    }
  }, [deadline]);

  const deadlineTodos = items
    ? items
        .filter((t) => t.deadlineTypes?.includes(deadline.type))
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    : undefined;
  const mostUrgentTodoItem = deadlineTodos?.length ? deadlineTodos[0] : undefined;

  const openDrawer = async (event: React.MouseEvent<HTMLDivElement, MouseEvent>, index: number) => {
    event.stopPropagation();
    dispatch(
      setTodoListNavigation({
        indexToOpen: index,
        pageToOpen: 0,
        numPerPage: todoCount,
        loadNext: () => {
          return drawerFilterFunc().then((res) => ({
            items: res.data?.items ?? [],
            totalItems: res.data?.totalItems ?? 0,
          }));
        },
        todoCanIncludeInNavigation: (todo: WintSuperkollAPIModel.Todo) =>
          todo.deadlineTypes ? todo.deadlineTypes.length > 0 : false,
      })
    );

    navigate(`${UrlHelper.formatPath(`/Todo/${index}`)}`, {
      state: { background: location },
    });
  };

  const drawerFilterFunc = React.useCallback(async () => {
    return getMockApiResponse(deadlineTodos as WintSuperkollAPIModel.Todo[]);
  }, [todoCount]);

  const goToDeadlineReport = async () => {
    navigate(
      `${UrlHelper.formatPath(
        `/Reports/DeadlineReport?${RouteConstants.DeadlineQueryParams.DeadlineId}=${deadline.id}&deadlineTypes=${deadline.type}`
      )}`,
      {
        state: { background: location },
      }
    );
  };

  return (
    <Grid
      item
      container
      className={clsx(cssExports.stateBannerContainer, cssExports.urgentBannerContainer, props.className)}
      direction="row"
      onClick={goToDeadlineReport.bind(null)}
    >
      <Hidden lgDown>
        <Grid item className={cssExports.bannerImgContainer}>
          <React.Suspense fallback={<Skeleton width={148} style={{ background: "transparent" }}></Skeleton>}>
            <BannerImageLazy />
          </React.Suspense>
        </Grid>
      </Hidden>
      <Grid item xs className={cssExports.bannerActionsContainer}>
        <Grid container className={cssExports.bannerHeaderText}>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.DeadlineBannerTitle)}
        </Grid>
        <Grid container className={cssExports.bannerDescription}>
          <ul>
            {messages ? (
              messages.map((message, index) => {
                return (
                  <li key={index}>
                    <Icon className={cssExports.redColor} icon={IconDefinitionConstants.WValidateCheck} />
                    {" " + message}
                  </li>
                );
              })
            ) : (
              <></>
            )}
          </ul>
        </Grid>
        <Grid container>
          {mostUrgentTodoItem !== undefined ? (
            <Grid item onClick={(e) => openDrawer(e, 0)}>
              <TodoChipContainer todo={mostUrgentTodoItem} />
            </Grid>
          ) : (
            <></>
          )}
        </Grid>
      </Grid>
    </Grid>
  );
};

export default DeadlineReportWidget;
